import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead from 'Components/EnhancedTableHead'
import type { HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { ServiceProviderTypeValue } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Components/ServiceProviderTypeValue'
import type {
  FetchServiceProviderListParams,
  ServiceProvider
} from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderSlice'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import InfoIcon from '@mui/icons-material/Info'
import {
  type ChipProps,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Tooltip
} from '@mui/material'
import Chip from '@mui/material/Chip'
import { t } from 'ttag'

interface ServiceProviderListProps {
  rows: ServiceProvider[]
  isLoading: boolean
  params: FetchServiceProviderListParams
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
  onRowClick: (id: string) => void
  selectedServiceProviderId: string | undefined
}

function ServiceProviderList({
  rows,
  isLoading,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page,
  onRowClick,
  selectedServiceProviderId
}: Readonly<ServiceProviderListProps>) {
  const headers: HeadCell[] = [
    {
      id: 'serviceProviderId',
      numeric: false,
      disablePadding: false,
      label: t`Code`,
      sortable: true
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`Service provider name`,
      sortable: true
    },
    {
      id: 'active',
      numeric: false,
      disablePadding: false,
      label: t`Status`,
      sortable: true
    },
    {
      id: 'type',
      numeric: false,
      disablePadding: false,
      label: t`Type`,
      sortable: false
    },
    {
      id: 'createdTimestamp',
      numeric: false,
      disablePadding: false,
      label: t`Created`,
      sortable: true
    }
  ]

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  const chipPropsMapping: Record<string, Pick<ChipProps, 'color' | 'label'>> = {
    ACTIVE: { color: 'success', label: t`Active` },
    PASSIVE: { color: 'default', label: t`Passive` }
  }

  function getChipPropsByActivity(status: boolean) {
    return chipPropsMapping[status ? 'ACTIVE' : 'PASSIVE']
  }

  return (
    <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={orderBy}
              headCells={headers}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => {
                return (
                  <StyledTableRow
                    hover
                    tabIndex={-1}
                    key={row.service_provider_id}
                    selected={selectedServiceProviderId === row.service_provider_id}
                  >
                    <ClickableTableCell onClick={() => onRowClick(row.service_provider_id)}>
                      {showValueOrHyphen(row.service_provider_id)}
                    </ClickableTableCell>
                    <ClickableTableCell onClick={() => onRowClick(row.service_provider_id)}>
                      {showValueOrHyphen(row.name)}
                    </ClickableTableCell>
                    <NoWrapTableCell title={row.active ? t`Active` : t`Passive`}>
                      <Chip size="small" {...getChipPropsByActivity(row.active)} />
                      {row.activity_end_date && row.active && (
                        <Tooltip
                          title={t`Activity ends` + ' ' + datetimeFormatter(row.activity_end_date)}
                          placement="top"
                        >
                          <IconButton sx={{ p: 0, ml: 1 }}>
                            <InfoIcon color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={showValueOrHyphen(row.type)}>
                      <ServiceProviderTypeValue serviceProviderType={row.type} />
                    </NoWrapTableCell>
                    <NoWrapTableCell
                      title={datetimeFormatter(row.created_timestamp)}
                      component="th"
                      scope="row"
                    >
                      {datetimeFormatter(row.created_timestamp)}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}

export default ServiceProviderList
